import { XMarkIcon } from "@heroicons/react/24/solid";
import { Fragment, useEffect } from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useDebouncedCallback } from "use-debounce";
import { ZodError } from "zod";
import { TransformSelectInput } from "../../utils/transforms/transformSelectInput.ts";
import { Maybe } from "../../utils/types/maybe.ts";
import ErrorLabel from "./errorLabel.tsx";

type Entity = {
  id: string;
  name: string;
};

type Option = {
  label: string;
  value: string;
};

type Props = {
  name: string;
  label: string;
  transform: TransformSelectInput;
  queryFn: (nameFilter: string) => Promise<Entity[]>;
};

export default function SearchableMultiSelect({
  name,
  label,
  transform,
  queryFn,
}: Props) {
  const watch = useWatch({ name });
  const { formState } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    name,
  });

  const debouncedQueryFn = useDebouncedCallback(
    (inputValue: string, callback) => {
      queryFn(inputValue)
        .then((results) =>
          callback(
            results?.map((entity) => ({
              label: entity.name,
              value: entity.id,
            })) ?? [],
          ),
        )
        .catch((error) => {
          console.error(error);
          callback([]);
        });
    },
    250,
  );

  const fieldErrors = formState.errors[name];
  const buildErrorMessageAtIndex = (index: number): Maybe<string> => {
    if (!Array.isArray(fieldErrors)) {
      return null;
    }

    const fieldError = fieldErrors.at(index);
    if (fieldError == null) {
      return null;
    }

    if (fieldError.type === "invalid_type") {
      return "This field is required";
    }

    return Object.values(fieldError)
      .map((error) => (error as ZodError)?.message)
      .join(", ");
  };

  useEffect(() => {
    if (Array.isArray(fields) && fields.length === 0) {
      append(null);
    }
  }, [fields]);

  const canAddAnother =
    Array.isArray(watch) && watch.every((value: Option) => value != null);

  return (
    <div className="sm:col-span-4">
      <label
        htmlFor={name}
        className="block text-sm font-medium leading-6 text-gray-900"
      >
        {label}
      </label>
      {fields.map((item, index) => (
        <Fragment key={item.id}>
          <div className="mt-1 flex gap-2 border-0 bg-transparent py-1.5">
            <Controller
              name={`${name}.${index}`}
              render={({ field }) => (
                <AsyncCreatableSelect
                  className="!text-gray-900 sm:text-sm sm:leading-6 flex-grow"
                  classNames={{
                    control: (state) =>
                      state.isFocused
                        ? "!border-sky-600 !shadow-sm"
                        : "border-grey-300",
                    option: () => "!text-gray-900",
                  }}
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary: "#bae6fd",
                    },
                  })}
                  placeholder={"Search..."}
                  noOptionsMessage={() => "Please provide a search term"}
                  cacheOptions
                  defaultOptions={[]}
                  loadOptions={(inputValue, callback) =>
                    debouncedQueryFn(inputValue, callback)
                  }
                  {...field}
                  onChange={(e) => {
                    field.onChange(transform.output(e));
                  }}
                  value={transform.input(field.value)}
                />
              )}
            />
            {index !== 0 ? (
              <button
                type="button"
                className="flex-none p-1.5 border-none border-none:focus"
                onClick={() => remove(index)}
              >
                <XMarkIcon className="h-5 w-5 text-gray-400" />
              </button>
            ) : (
              /* Spacing to fill button's width */
              <div className="flex-none p-1.5">
                <div className="w-5" />
              </div>
            )}
          </div>
          {buildErrorMessageAtIndex(index) && (
            <ErrorLabel message={buildErrorMessageAtIndex(index)} />
          )}
        </Fragment>
      ))}
      {canAddAnother && (
        <a
          className="text-sm font-medium text-sky-500 hover:text-sky-700 cursor-pointer"
          onClick={() => {
            append(null);
          }}
        >
          Add another
        </a>
      )}
    </div>
  );
}
